import { Language, NumberFormatPreference, userConfigStore } from "@/store/user-config-store";
import { useStore } from "zustand";

export type NumberFormatLocale = NumberFormatPreference.German | NumberFormatPreference.EnglishUS;

export interface LocalizedNumberOptions {
  locale?: string;
  allowNegative?: boolean;
  integerOnly?: boolean;
}

export interface NumberSeparators {
  decimalSeparator: string;
  groupSeparator: string;
}

const NORMALIZED_NUMBER_PATTERN = /^-?\d+(?:\.\d+)?$/;
const WHITESPACE_PATTERN = /[\s\u00a0\u202f]/;

export function resolveLanguageNumberLocale(language: Language | string | null | undefined): NumberFormatLocale {
  return String(language ?? "")
    .toLowerCase()
    .startsWith(Language.English)
    ? NumberFormatPreference.EnglishUS
    : NumberFormatPreference.German;
}

export function resolveNumberFormatLocale(
  preference: NumberFormatPreference | null | undefined,
  language: Language | string | null | undefined,
): NumberFormatLocale {
  if (preference === NumberFormatPreference.German || preference === NumberFormatPreference.EnglishUS) {
    return preference;
  }

  return resolveLanguageNumberLocale(language);
}

export function getCurrentNumberFormatLocale(): NumberFormatLocale {
  const { numberFormatPreference, language } = userConfigStore.getState();
  return resolveNumberFormatLocale(numberFormatPreference, language);
}

export function useNumberFormatLocale(): NumberFormatLocale {
  return useStore(userConfigStore, (state) => resolveNumberFormatLocale(state.numberFormatPreference, state.language));
}

export function useLanguageNumberLocale(): NumberFormatLocale {
  return useStore(userConfigStore, (state) => resolveLanguageNumberLocale(state.language));
}

export interface CompactCountResult {
  display: string;
  full: string;
  isCompact: boolean;
}

const COMPACT_COUNT_THRESHOLD = 1000;

const COMPACT_COUNT_SUFFIXES: Record<
  NumberFormatLocale,
  { space: string; thousand: string; million: string; billion: string }
> = {
  [NumberFormatPreference.EnglishUS]: { space: "", thousand: "K", million: "M", billion: "B" },
  [NumberFormatPreference.German]: { space: "\u00a0", thousand: "Tsd.", million: "Mio.", billion: "Mrd." },
};

/**
 * Formats an entry count for display in table headers.
 *
 * Values below 1000 are returned as a full localized number. Larger values are abbreviated with a single
 * decimal place and a locale-aware suffix (en: K/M/B, de: Tsd./Mio./Mrd.). The full localized number is
 * always returned as well so callers can show it in a tooltip when the value is compacted.
 */
export function formatEntriesCount(value: number, locale: NumberFormatLocale): CompactCountResult {
  const full = new Intl.NumberFormat(locale).format(value);

  if (value < COMPACT_COUNT_THRESHOLD) {
    return { display: full, full, isCompact: false };
  }

  const suffixes = COMPACT_COUNT_SUFFIXES[locale];

  let divisor: number;
  let suffix: string;
  if (value >= 1_000_000_000) {
    divisor = 1_000_000_000;
    suffix = suffixes.billion;
  } else if (value >= 1_000_000) {
    divisor = 1_000_000;
    suffix = suffixes.million;
  } else {
    divisor = 1000;
    suffix = suffixes.thousand;
  }

  const mantissa = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / divisor);

  return { display: `${mantissa}${suffixes.space}${suffix}`, full, isCompact: true };
}

export function getNumberSeparators(locale: string): NumberSeparators {
  const parts = new Intl.NumberFormat(locale).formatToParts(1000.1);
  const decimalSeparator = parts.find((part) => part.type === "decimal")?.value ?? ".";
  const groupSeparator = parts.find((part) => part.type === "group")?.value ?? ",";

  return { decimalSeparator, groupSeparator };
}

export function parseLocalizedNumberInput(value: string, options: LocalizedNumberOptions = {}): number {
  const normalizedValue = normalizeLocalizedNumberInput(value, options);

  if (normalizedValue === null || normalizedValue.length === 0) {
    return Number.NaN;
  }

  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return Number.NaN;
  }

  if (options.allowNegative === false && parsedValue < 0) {
    return Number.NaN;
  }

  if (options.integerOnly && !Number.isInteger(parsedValue)) {
    return Number.NaN;
  }

  return parsedValue;
}

export function isLocalizedNumberInput(value: string, options: LocalizedNumberOptions = {}): boolean {
  return Number.isFinite(parseLocalizedNumberInput(value, options));
}

function normalizeLocalizedNumberInput(value: string, options: LocalizedNumberOptions): string | null {
  const trimmedValue = value.trim();

  if (trimmedValue.length === 0) {
    return "";
  }

  const locale = options.locale ?? getCurrentNumberFormatLocale();
  const separators = getNumberSeparators(locale);

  if (WHITESPACE_PATTERN.test(trimmedValue)) {
    return null;
  }

  const groupedValue = normalizeGroupedNumber(trimmedValue, separators);

  if (groupedValue !== null) {
    return groupedValue;
  }

  const localizedDecimalValue = normalizeLocalizedDecimalNumber(trimmedValue, separators);

  if (localizedDecimalValue !== null) {
    return localizedDecimalValue;
  }

  if (NORMALIZED_NUMBER_PATTERN.test(trimmedValue)) {
    return trimmedValue;
  }

  return null;
}

function normalizeGroupedNumber(value: string, separators: NumberSeparators): string | null {
  if (!value.includes(separators.groupSeparator) || !isGroupedNumberPattern(value, separators)) {
    return null;
  }

  return value.split(separators.groupSeparator).join("").replace(separators.decimalSeparator, ".");
}

function normalizeLocalizedDecimalNumber(value: string, separators: NumberSeparators): string | null {
  if (separators.decimalSeparator === "." || !value.includes(separators.decimalSeparator)) {
    return null;
  }

  if (value.includes(separators.groupSeparator)) {
    return null;
  }

  const normalizedValue = value.replace(separators.decimalSeparator, ".");
  return NORMALIZED_NUMBER_PATTERN.test(normalizedValue) ? normalizedValue : null;
}

function isGroupedNumberPattern(value: string, separators: NumberSeparators): boolean {
  if (separators.groupSeparator.length === 0) {
    return false;
  }

  const escapedGroupSeparator = escapeRegExp(separators.groupSeparator);
  const escapedDecimalSeparator = escapeRegExp(separators.decimalSeparator);
  return new RegExp(`^-?\\d{1,3}(?:${escapedGroupSeparator}\\d{3})+(?:${escapedDecimalSeparator}\\d+)?$`).test(value);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
